/* ============================================================
 * wishlist-hero-position-fix.js
 * ------------------------------------------------------------
 * Force Wishlist Hero "floating" buttons on collection product
 * cards into the BOTTOM-RIGHT corner of the product image.
 *
 * Why this file exists:
 *   - The Wishlist Hero app embed (`bundle2.js`) writes
 *     positioning via INLINE STYLE on .wishlisthero-floating
 *     after first paint, so app-block CSS / Additional-HTML CSS
 *     loses the specificity battle (inline > stylesheet, even
 *     with !important when both sides use !important).
 *   - The Liquid snippet (`buttonClass=wishlisthero-floating`)
 *     and the embedded stylesheet position the button TOP-RIGHT
 *     (right:5px / top:5px), but the live runtime ends up
 *     TOP-LEFT for this theme — meaning bundle2.js mutates the
 *     inline style on top of the stylesheet rules.
 *
 * Strategy (belt + braces):
 *   1) Inject one <style> with .wlh-bottom-right-fixed rules
 *      that hard-pin position to bottom-right with !important.
 *   2) On every detected floating button:
 *        - clear inline top/left/right/bottom/transform/margin
 *        - add the wlh-bottom-right-fixed class
 *        - also re-set inline bottom/right/top/left/position to
 *          guarantee inline-style cascade win
 *        - tag with data-wlh-position-fixed="1" for fast skip
 *   3) MutationObserver watches body subtree for:
 *        - new nodes (app injects buttons async)
 *        - attribute changes on `style` (app re-writes positions)
 *      Re-applies fix if either fires and a button is off-mark.
 *   4) Idempotent: re-running this file or re-firing observer
 *      on the same node does not stack listeners or styles.
 *   5) Guarded to collection pages only.
 *   6) Wrapped in try/catch so an upstream class rename can't
 *      break the storefront.
 * ============================================================ */
(function () {
  'use strict';

  // ---- No path-based guard --------------------------------
  // Hearts appear on collection grids AND on PDPs (recommended
  // products / related products carousels) AND on the home page's
  // featured-collection sections. Restricting to /collections/
  // only fixes the first, leaves PDPs and home with top-left
  // hearts. The Wishlist Hero selectors are specific enough
  // (`.wishlist-hero-custom-button.wishlisthero-floating`) that
  // running this script on a non-wishlist page is a cheap no-op:
  // querySelectorAll returns 0, the MutationObserver only acts
  // on matching new nodes, and the pinButton function is itself
  // a no-op if called with a non-matching element.

  // Singleton install guard — protect against being injected
  // twice (e.g. theme + app embed both loading the fix).
  if (window.__wlhBottomRightFixInstalled) return;
  window.__wlhBottomRightFixInstalled = true;

  // ---- Constants -------------------------------------------
  var STYLE_TAG_ID = 'wlh-bottom-right-fixed-style';
  var FIXED_CLASS = 'wlh-bottom-right-fixed';
  var DONE_ATTR = 'data-wlh-position-fixed';
  // Selectors covering current app DOM + defensive variants
  // observed across Wishlist Hero versions.
  var SELECTOR = [
    '.wishlist-hero-custom-button.wishlisthero-floating',
    '.wishlisthero-floating',
    '.wishlist-hero-custom-button',
    '[class*="wishlist"][class*="floating"]'
  ].join(', ');

  // Target offsets (px). Mirrors the existing top-right values
  // the app uses (5px) so visual rhythm is preserved.
  var OFFSET_PX = '10px';

  // ---- One-time stylesheet injection -----------------------
  function injectStyleOnce() {
    if (document.getElementById(STYLE_TAG_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_TAG_ID;
    style.type = 'text/css';
    // !important on every position prop so we win against the
    // app's stylesheet rules; the inline-style override below
    // is what wins against the app's inline-style writes.
    style.appendChild(document.createTextNode(
      // NOTE: previous attempts to fix heart-click-goes-to-PDP via CSS
      // alone (z-index lift, pointer-events:none on .card__heading
      // a::after) both broke other parts of the card — z-index hid
      // the ROOM pills, pointer-events made image+badge unclickable.
      // The overlay stays. Heart clicks WILL still navigate to PDP
      // until either (a) Wishlist Hero's `EnableCollection` is toggled
      // ON in app settings (their bundle2.js then attaches a
      // stopPropagation click handler), or (b) we wire a non-blocking
      // handler from this script. Position/size/color fixes still
      // work; this only affects click-to-add behavior.
      '.' + FIXED_CLASS + '{' +
        'position:absolute !important;' +
        'top:auto !important;' +
        'left:auto !important;' +
        'right:' + OFFSET_PX + ' !important;' +
        'bottom:' + OFFSET_PX + ' !important;' +
        // Critical: shrink container to icon size. Without this the
        // .wishlisthero-floating div inherits parent dimensions
        // (~217x271 px = product image) and the visible SVG renders
        // at the box's top-left = card top-left. Forcing auto makes
        // the container hug its actual heart-icon content.
        'width:auto !important;' +
        'height:auto !important;' +
        'min-width:0 !important;' +
        'min-height:0 !important;' +
        'max-width:none !important;' +
        'max-height:none !important;' +
        'transform:none !important;' +
        'margin:0 !important;' +
        // iOS Safari does NOT dispatch `click` events on taps for non-
        // interactive <div> elements that lack `cursor:pointer`. The
        // hearts are <div>, not <button>/<a>, and the underlying app
        // CSS does not reliably set cursor across Wishlist Hero versions.
        // Forcing it here ensures the document-level capture-phase
        // click interceptor below actually receives the event on iOS.
        'cursor:pointer !important;' +
        // Disable iOS double-tap-zoom delay on the heart — the tap
        // should register as an instant click, not wait 300ms for a
        // possible second tap to become a zoom gesture.
        'touch-action:manipulation !important;' +
        'z-index:21;' +
      '}' +
      // Pulse animation fired on click for visible feedback —
      // makes it obvious the click registered even when bundle2
      // doesn't show a toast (e.g. when EnableCollection is off).
      '@keyframes wlh-pulse{' +
        '0%{transform:scale(1)}' +
        '40%{transform:scale(1.4)}' +
        '100%{transform:scale(1)}' +
      '}' +
      '.wlh-pulse{animation:wlh-pulse 380ms ease-out !important;}' +
      // ---- Product-page (PDP) button ---------------------------
      // The Wishlist Hero app settings stamp `wlh-bottom-right` onto
      // EVERY wishlist button and inject (via extraHTMLToAppend):
      //
      //   .wlh-bottom-right{position:absolute !important;
      //     inset:auto 10px 10px auto !important;z-index:10 !important}
      //
      // That is right for the floating heart over a product image, and
      // wrong for the PDP button, which is a full-width labelled button
      // and the container's ONLY child. Absolute positioning takes it
      // out of flow, so the container has no in-flow content left and
      // `height:auto` resolves to 0 — the button then paints outside
      // its own box, over whatever follows (here <share-button>, 264px
      // down). Measured 2026-08-27 on stage 136812527864:
      //
      //   before  container h=0   button position:absolute  share top=652
      //   after   container h=58  button position:relative  share top=710
      //
      // Forcing height on the CONTAINER cannot work — an empty-in-flow
      // box is 0 by definition, which is why the earlier height:auto
      // rule (#391) computed correctly and changed nothing. The fix has
      // to put the button BACK IN FLOW, so the container measures it.
      //
      // Scoped to `> button.wlh-bottom-right` inside the PDP container
      // specifically. The card hearts are DIVs carrying our own
      // `wlh-bottom-right-fixed` and live nowhere near this container;
      // verified in-browser that this selector matches 0 elements on a
      // collection page rendering 16 of them.
      //
      // ROOT CAUSE, not fixed here: `additionalWLButtonCssClasses` in
      // the Wishlist Hero app settings should not put the floating
      // class on the PDP button at all, and its injected CSS should be
      // scoped (e.g. `.img-wrapper .wlh-bottom-right`). That lives in
      // settings_data.json, which is `ignore`d on push in every
      // environment, so it can only be changed in the app admin. Until
      // then this override is what keeps the page correct.
      '.wishlisthero-product-page-button-container>button.wlh-bottom-right{' +
        // `relative`, NOT `static`. Static was shipped in #392 and caused a
        // worse bug than the one it fixed: the theme's .btn class draws its
        // border with ::before/::after pinned at inset:0, and those resolve
        // against the nearest POSITIONED ancestor. Making the button static
        // removed it as their containing block, so both pseudo-elements
        // escaped to <product-info> and stretched to its full height —
        // measured 602px against a 58px button. That painted a border around
        // the entire product column AND made the whole column hit-test as
        // the wishlist button (elementFromPoint returned it at the title,
        // the price, and the description). `relative` takes the button out
        // of the app's absolute positioning exactly the same way while
        // keeping it a containing block: pseudo-elements measured 56px.
        'position:relative !important;' +
        'inset:auto !important;' +
        // The app sets `border-style:none` INLINE on this button, so the
        // outline has to be re-asserted with !important to beat it. Values
        // mirror the theme's Add to cart button measured on the same page
        // (399x58, radius 17px, accent rgb(71,161,97)) so the two read as a
        // matched primary/secondary pair rather than two unrelated controls.
        // Height is pinned because the app's inline style sets 40px, which
        // would leave the bordered box shorter than its own 58px row.
        'border:1px solid rgb(71,161,97) !important;' +
        'border-radius:17px !important;' +
        'height:58px !important;' +
        // The app's inline opacity:0.9 washes the border out against white.
        'opacity:1 !important;' +
      '}' +
      // Two states across all hearts (card + header): outline / filled.
      // Strategy: ALWAYS draw a green stroke on the path. The path's
      // own fill="currentColor" attribute makes its fill inherit from
      // the parent's CSS `color` — which we toggle per state via
      // applyStateFill() inline JS. No CSS-attribute-selector fight.
      '.' + FIXED_CLASS + ' svg{overflow:visible !important;}' +
      '.' + FIXED_CLASS + ' svg path,' +
      '.' + FIXED_CLASS + ' svg polygon,' +
      '.' + FIXED_CLASS + ' svg use{' +
        'transition:fill 180ms ease, color 180ms ease;' +
        'stroke:#47A161 !important;' +
        'stroke-width:40px !important;' +
        'paint-order:stroke fill !important;' +
        'stroke-linejoin:round !important;' +
        'stroke-linecap:round !important;' +
      '}' +
      // Defeat the desktop media-query rule shipped with the
      // app embed that yanks the button to left:50% on wide
      // screens (.product-item__link.product-item__image--margins
      // .wishlisthero-floating { left:50% !important; margin-left:-295px }).
      '@media(min-width:1300px){' +
        '.product-item__link.product-item__image--margins ' +
        '.wishlisthero-floating.' + FIXED_CLASS + ',' +
        '.' + FIXED_CLASS + '{' +
          'left:auto !important;' +
          'right:' + OFFSET_PX + ' !important;' +
          'bottom:' + OFFSET_PX + ' !important;' +
          'top:auto !important;' +
          'margin:0 !important;' +
        '}' +
      '}'
    ));
    (document.head || document.documentElement).appendChild(style);
  }

  // ---- Per-button fix --------------------------------------
  // `force` = true ignores the DONE_ATTR short-circuit and
  // re-applies anyway (used when MutationObserver detects the
  // app has re-written our inline style).
  function pinButton(el, force) {
    if (!el || el.nodeType !== 1) return;
    try {
      // ALWAYS apply state-fill (the visual that gets stripped by
      // bundle2/CSS most often) — even if position is already pinned.
      // Moved OUTSIDE the idempotency short-circuit so state-fill is
      // re-applied on every sweep regardless of the position cache.
      // getEffectiveState() returns the optimistic pinned state during
      // the grace window — so re-renders by bundle2 can't strip our green.
      try { applyStateFill(el, getEffectiveState(el)); } catch (eA) {}

      // Cheap idempotency check for POSITION only — if position is
      // already pinned, skip the position reset (still ran fill above).
      if (!force && el.getAttribute(DONE_ATTR) === '1') {
        var s = el.style;
        if (s.bottom === OFFSET_PX &&
            s.right === OFFSET_PX &&
            (s.top === 'auto' || s.top === '') &&
            (s.left === 'auto' || s.left === '')) {
          return; // position already correct
        }
      }
      // Clear the inline positional props the app set.
      var props = ['top', 'left', 'right', 'bottom',
                   'transform', 'margin', 'margin-left',
                   'margin-right', 'margin-top', 'margin-bottom'];
      for (var i = 0; i < props.length; i++) {
        el.style.removeProperty(props[i]);
      }
      // Add the class for stylesheet fallback.
      if (!el.classList.contains(FIXED_CLASS)) {
        el.classList.add(FIXED_CLASS);
      }
      // Force inline values to win the cascade against any
      // future bundle2.js writes. setProperty with 'important'
      // is the only way to put `!important` into inline style.
      el.style.setProperty('position', 'absolute', 'important');
      el.style.setProperty('top', 'auto', 'important');
      el.style.setProperty('left', 'auto', 'important');
      el.style.setProperty('right', OFFSET_PX, 'important');
      el.style.setProperty('bottom', OFFSET_PX, 'important');
      el.style.setProperty('transform', 'none', 'important');
      el.style.setProperty('margin', '0', 'important');
      // Make the heart visually thicker without a transform-scale
      // (transforms create a new stacking context that confused
      // Dawn's .card__content overlay and broke card clicks).
      // Explicit container size with width/height instead.
      el.style.setProperty('width', '36px', 'important');
      el.style.setProperty('height', '36px', 'important');
      if (!el.getAttribute('data-wlh-item-state')) {
        // Seed from pendingFlips so a heart node replaced mid-click
        // re-emerges in its pinned state, not default-empty.
        try {
          var seedPid = el.getAttribute('data-wlh-id');
          var seedPinned = peekPendingFlip(seedPid);
          el.setAttribute('data-wlh-item-state', seedPinned || 'empty');
        } catch (eSeed) {
          el.setAttribute('data-wlh-item-state', 'empty');
        }
      }
      el.setAttribute(DONE_ATTR, '1');
    } catch (perBtnErr) {
      // Swallow — never break the storefront over one card.
    }
  }

  // ---- Wishlist click handler ------------------------------
  // Stops the click from bubbling to Dawn's full-card title link
  // (which would navigate to PDP), then invokes Wishlist Hero's
  // SDK directly with the exact PascalCase payload its validator
  // expects (reverse-engineered from bundle2.js: ProductId,
  // ProductVariantId, ProductTitle, ProductPrice, ProductLink,
  // ProductImage, WishlistHash, CustomerId, ButtonClassElement,
  // ButtonMode). The SDK dispatches an internal CustomEvent which
  // the rest of bundle2.js listens for and persists.
  function wishlistClickHandler(e) {
    try {
      e.stopPropagation();
      e.preventDefault();

      var el = e.currentTarget;
      var sdk = window.WishListHero_SDK;
      if (!sdk || !sdk.AddWishListItem) return;

      var pid     = parseInt(el.getAttribute('data-wlh-id'), 10);
      var variant = parseInt(el.getAttribute('data-wlh-variantid'), 10);
      var name    = el.getAttribute('data-wlh-name') || '';
      var priceN  = parseFloat(el.getAttribute('data-wlh-price')) || 0;
      var image   = el.getAttribute('data-wlh-image') || '';
      var link    = el.getAttribute('data-wlh-link') || '';
      var hash    = (sdk.GetWishListHash && sdk.GetWishListHash()) || '';
      // CustomerId MUST be a number for the SDK validator. For
      // logged-in customers it's the real customer ID; for guests
      // (where GetCustomerId returns null/undefined) the SDK
      // accepts 0 as a guest sentinel. Empty string would NaN.
      var rawCust = sdk.GetCustomerId && sdk.GetCustomerId();
      var custId  = (typeof rawCust === 'number') ? rawCust :
                    (rawCust ? parseInt(rawCust, 10) || 0 : 0);
      var mode    = el.getAttribute('data-wlh-mode') || 'icon_only';

      var payload = {
        ProductId: pid,
        ProductVariantId: variant,
        ProductTitle: name,
        ProductPrice: priceN,
        ProductLink: link,
        ProductImage: image,
        WishlistHash: hash,
        CustomerId: custId,
        ButtonClassElement: el,
        ButtonMode: mode
      };

      // Decide add vs remove based on current wishlist state.
      var inWishlist = false;
      try {
        var items = sdk.GetWishListItems && sdk.GetWishListItems();
        if (Array.isArray(items)) {
          inWishlist = items.some(function (it) {
            // Items use various id shapes — check broadly
            var iid = it && (it.ProductId || it.productId || it.id);
            return String(iid) === String(pid);
          });
        }
      } catch (eGet) {}

      if (inWishlist && sdk.DeleteWishListItem) {
        sdk.DeleteWishListItem(payload);
      } else {
        sdk.AddWishListItem(payload);
      }
    } catch (clickErr) {
      // Never break the storefront — silently no-op if the SDK
      // explodes in an unexpected way.
    }
  }

  // Sweep the whole document (or a subtree) for buttons.
  function sweep(root) {
    try {
      var scope = (root && root.querySelectorAll) ? root : document;
      var nodes = scope.querySelectorAll(SELECTOR);
      for (var i = 0; i < nodes.length; i++) {
        pinButton(nodes[i], false);
      }
    } catch (sweepErr) {
      // Selector parse failure or detached scope — ignore.
    }
  }

  // ---- MutationObserver: catch async-injected buttons + ----
  //                       style attribute rewrites           --
  var observer;
  function installObserver() {
    if (observer) return;
    try {
      observer = new MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var mut = mutations[m];
          if (mut.type === 'childList') {
            // New nodes added — scan them.
            for (var n = 0; n < mut.addedNodes.length; n++) {
              var node = mut.addedNodes[n];
              if (node.nodeType !== 1) continue;
              // Is the added node itself a button?
              if (node.matches && node.matches(SELECTOR)) {
                pinButton(node, false);
              }
              // Or does it contain buttons?
              if (node.querySelectorAll) {
                sweep(node);
              }
            }
          } else if (mut.type === 'attributes') {
            // App may have re-written our inline style. If the
            // mutated node is one of our buttons AND the current
            // style is NOT what we want, re-pin. Skipping the
            // re-pin when style is already correct breaks an
            // infinite loop: our own pinButton sets style/class,
            // which fires this observer, which would re-pin
            // forever if `force:true` bypassed the check.
            var target = mut.target;
            if (target && target.nodeType === 1 &&
                target.matches && target.matches(SELECTOR)) {
              var s = target.style;
              var alreadyOurs =
                s.bottom === OFFSET_PX &&
                s.right === OFFSET_PX &&
                (s.top === 'auto' || s.top === '') &&
                (s.left === 'auto' || s.left === '');
              if (!alreadyOurs) {
                pinButton(target, true);
              }
            }
          }
        }
      });
      observer.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    } catch (obsErr) {
      // No observer support or document.body missing — degrade
      // gracefully to the initial-sweep-only path.
    }
  }

  // ---- Document-level capture-phase click interceptor ------
  // ONE listener on document, capture phase. Catches clicks
  // BEFORE they bubble to Dawn's title-link overlay. Only
  // intercepts when target is inside a wishlist heart — all
  // other clicks pass through unchanged (image, badge, title,
  // anywhere else on the card).
  //
  // The SDK call is deferred via setTimeout(0) so any failure
  // or hang in the SDK can't lock up the click event loop.
  // Fire-and-forget — we don't await the returned Promise.
  function installClickInterceptor() {
    if (window.__wlhClickInterceptorInstalled) return;
    window.__wlhClickInterceptorInstalled = true;

    document.addEventListener('click', function (e) {
      // COORD-BASED check, not target-based. Dawn's full-card title
      // link <a> sits ABOVE the heart in elementFromPoint hit-test
      // order — even though the heart visually paints above — so
      // e.target on real mouse clicks is the <a>, not the heart.
      // Bounding-box test catches this: if click coords fall within
      // ANY heart's rect, we treat it as a heart click.
      var heart = null;
      try {
        var hearts = document.querySelectorAll(SELECTOR);
        for (var hi = 0; hi < hearts.length; hi++) {
          var r = hearts[hi].getBoundingClientRect();
          if (e.clientX >= r.left && e.clientX <= r.right &&
              e.clientY >= r.top  && e.clientY <= r.bottom) {
            heart = hearts[hi];
            break;
          }
        }
      } catch (eFind) { return; }
      if (!heart) return; // Click not over any heart — pass through

      // It IS a heart click. Stop it from bubbling to the
      // title-link overlay (which would navigate to PDP).
      e.stopPropagation();
      e.preventDefault();

      // OPTIMISTIC state toggle — flip data-wlh-item-state attribute
      // AND set inline fill/stroke on the SVG path directly. Inline
      // styles win against the path's fill="currentColor" presentation
      // attribute deterministically; CSS attribute selectors don't
      // always override that on every browser.
      //
      // Record the flip in pendingFlips so refineHeartStates won't
      // revert it during the SDK round-trip — `wishlist-hero-wishlist-
      // items-from-server` often fires before the new item is in the
      // SDK's local items cache, which would otherwise re-paint the
      // heart back to empty until a page refresh sees server truth.
      try {
        var currentState = heart.getAttribute('data-wlh-item-state') || 'empty';
        var newState = currentState === 'filled' ? 'empty' : 'filled';
        heart.setAttribute('data-wlh-item-state', newState);
        applyStateFill(heart, newState);
        var pidForFlip = parseInt(heart.getAttribute('data-wlh-id'), 10);
        if (!isNaN(pidForFlip)) recordPendingFlip(pidForFlip, newState);
      } catch (eToggle) {}

      // Visual feedback — pulse the SVG so user sees the click
      // registered. Class removed after the animation completes
      // so the next click animates again.
      try {
        var svg = heart.querySelector('svg');
        if (svg) {
          svg.classList.remove('wlh-pulse');
          // Force reflow so re-adding the class restarts the animation
          // eslint-disable-next-line no-unused-expressions
          svg.offsetWidth;
          svg.classList.add('wlh-pulse');
          setTimeout(function () { svg.classList.remove('wlh-pulse'); }, 400);
        }
      } catch (eAnim) {}

      // Defer SDK call off the click event loop so a hung SDK
      // can't make the page unresponsive to other clicks.
      setTimeout(function () {
        try {
          var sdk = window.WishListHero_SDK;
          if (!sdk || !sdk.AddWishListItem) return;
          var pid     = parseInt(heart.getAttribute('data-wlh-id'), 10);
          var variant = parseInt(heart.getAttribute('data-wlh-variantid'), 10);
          var hash    = (sdk.GetWishListHash && sdk.GetWishListHash()) || '';
          var rawCust = sdk.GetCustomerId && sdk.GetCustomerId();
          var custId  = (typeof rawCust === 'number') ? rawCust :
                        (rawCust ? parseInt(rawCust, 10) || 0 : 0);
          var payload = {
            ProductId: pid,
            ProductVariantId: variant,
            ProductTitle: heart.getAttribute('data-wlh-name') || '',
            ProductPrice: parseFloat(heart.getAttribute('data-wlh-price')) || 0,
            ProductLink: heart.getAttribute('data-wlh-link') || '',
            ProductImage: heart.getAttribute('data-wlh-image') || '',
            WishlistHash: hash,
            CustomerId: custId,
            ButtonClassElement: heart,
            ButtonMode: heart.getAttribute('data-wlh-mode') || 'icon_only'
          };
          // Decide add vs remove
          var inWishlist = false;
          try {
            var items = sdk.GetWishListItems && sdk.GetWishListItems();
            if (Array.isArray(items)) {
              inWishlist = items.some(function (it) {
                var iid = it && (it.ProductId || it.productId || it.id);
                return String(iid) === String(pid);
              });
            }
          } catch (eGet) {}
          // CRITICAL ORDERING: 1st-party mirror BEFORE the vendor SDK call.
          //
          // Wishlist Hero's bundle2.js may throw synchronously inside
          // AddWishListItem on Safari when GetWishListHash() returns null
          // (no guest identity). The outer try/catch swallows the throw,
          // and if lsAdd lived AFTER the SDK call inside the same try, the
          // throw would skip it — the user's click would visually fill the
          // heart but persist NOTHING (neither cloud nor local). Bug
          // observed empirically 2026-06-11 on hringekjan.is in real Safari.
          //
          // By writing to 1st-party localStorage FIRST, the Safari case is
          // safe whether the SDK throws, succeeds, or no-ops. Chrome users
          // get the (still-correct) cloud write right after; the merge in
          // patched GetWishListItems dedupes the redundant lsAdd entry.
          if (inWishlist) {
            lsDelete(payload.ProductId);
          } else {
            lsAdd(payload);
          }
          // SDK call — best-effort, isolated try/catch so vendor throws
          // can't take down the rest of the handler.
          try {
            if (inWishlist && sdk.DeleteWishListItem) sdk.DeleteWishListItem(payload);
            else if (sdk.AddWishListItem) sdk.AddWishListItem(payload);
          } catch (eSdkCall) {
            // Vendor SDK threw (most common: Safari ITP — no hash → no
            // identity → AddWishListItem rejects). 1st-party mirror above
            // already captured the user's intent, so the modal will still
            // show the item via the patched GetWishListItems on next read.
          }
        } catch (eSdk) {
          // Outer guard — page stays responsive even if payload construction
          // throws (NaN ProductId, missing data-wlh-id, etc.).
        }
      }, 0);
    }, true); // CAPTURE phase — runs before bubble
  }

  // ---- Boot ------------------------------------------------
  // ---- Direct fill control (path-swap + multi-vector) -----
  // ROOT CAUSE captured 2026-06-10 in Chrome DOM probe:
  //   Wishlist Hero renders the heart icon via FontAwesome inline SVG
  //   with `data-prefix="far"` (outline) for empty and `data-prefix="fas"`
  //   (solid) for filled. The TWO STATES USE DIFFERENT PATH `d` DATA:
  //     - far/fa-heart  — a RING (outer curve + inner curve via second M);
  //                       fill paints only the thin border between them
  //     - fas/fa-heart  — a single closed silhouette; fill paints whole heart
  //   Earlier versions only set fill="#47A161" on the empty-state path,
  //   which produced no visible fill (the ring sliver was hidden under the
  //   stroke-width=40 stroke). The fix is to ALSO swap the path `d` when
  //   the state flips, then fill works the way every other path does.
  var FA_HEART_OUTLINE_D = 'M458.4 64.3C400.6 15.7 311.3 23 256 79.3 200.7 23 111.4 15.6 53.6 64.3-21.6 127.6-13.2 257.8 36.1 308.1l192.5 192.5c2.4 2.4 5.5 3.7 8.4 3.7s6.0-1.3 8.4-3.7l192.5-192.5c49.3-50.3 57.7-180.5-19.5-243.8zM421.3 287.7L256 453 90.7 287.7c-30.5-30.5-44.2-114.7 6.5-158 38.6-32.9 100.4-25.6 145.7 21.4l13.1 13.6 13.1-13.6c45.3-47 107.1-54.4 145.7-21.4 50.7 43.3 36.9 127.5 6.5 158z';
  var FA_HEART_SOLID_D   = 'M462.3 62.6C407.5 15.9 326 24.3 275.7 76.2L256 96.5l-19.7-20.3C186.1 24.3 104.5 15.9 49.7 62.6c-62.8 53.6-66.1 149.8-9.9 207.9l193.5 199.8c12.5 12.9 32.8 12.9 45.3 0l193.5-199.8c56.3-58.1 53-154.3-9.8-207.9z';

  function applyStateFill(heart, state) {
    try {
      var filled = (state === 'filled');
      var fillValue   = filled ? '#47A161' : 'none';
      var strokeValue = '#47A161';
      var pathD       = filled ? FA_HEART_SOLID_D : FA_HEART_OUTLINE_D;
      var strokeW     = filled ? '0' : '40';   // solid needs no extra outline

      // Container colour (defensive — currentColor inheritance)
      heart.style.setProperty('color', strokeValue, 'important');

      // SVG-level overrides: swap data-prefix (cosmetic, but keeps WLH
      // diagnostics consistent) and override the inline `color` attr that
      // WLH writes on the <svg> itself (e.g. `color="rgba(37,39,77,1)"`) —
      // that attr is what makes currentColor resolve to navy in the
      // computed style, so we must overwrite at the SVG node too.
      var svg = heart.querySelector('svg');
      if (svg) {
        svg.setAttribute('data-prefix', filled ? 'fas' : 'far');
        svg.style.setProperty('color', strokeValue, 'important');
      }

      var paths = heart.querySelectorAll('svg path');
      for (var i = 0; i < paths.length; i++) {
        paths[i].setAttribute('d', pathD);   // ← KEY: swap geometry
        paths[i].setAttribute('fill', fillValue);
        paths[i].setAttribute('stroke', strokeValue);
        paths[i].setAttribute('stroke-width', strokeW);
        paths[i].setAttribute('paint-order', 'stroke fill');
        paths[i].setAttribute('stroke-linejoin', 'round');
        paths[i].setAttribute('stroke-linecap', 'round');
        paths[i].style.setProperty('fill', fillValue === 'none' ? 'transparent' : fillValue, 'important');
        paths[i].style.setProperty('stroke', strokeValue, 'important');
        paths[i].style.setProperty('transition', 'fill 180ms ease', 'important');
      }
      // Defensive: paint any non-path shapes (legacy WLH markup may
      // include polygon/use/circle/rect; current FA SVG does not).
      var others = heart.querySelectorAll('svg polygon, svg use, svg circle, svg rect');
      for (var j = 0; j < others.length; j++) {
        others[j].setAttribute('fill', fillValue);
        others[j].setAttribute('stroke', strokeValue);
        others[j].style.setProperty('fill', fillValue === 'none' ? 'transparent' : fillValue, 'important');
        others[j].style.setProperty('stroke', strokeValue, 'important');
      }
    } catch (e) {}
  }

  // ---- Optimistic-flip grace window ------------------------
  // After a user clicks a heart we paint the new state instantly,
  // but the SDK round-trip races ahead in several ways:
  //   1. `wishlist-hero-wishlist-items-from-server` event fires
  //      before the new item is in the SDK's local items cache
  //   2. bundle2.js writes data-wlh-item-state="empty" on its own
  //      heart re-render (server hasn't returned yet so it thinks
  //      the heart is empty)
  //   3. bundle2.js may replace the heart node entirely, losing
  //      our attribute write
  // The pendingFlips map keys by ProductId (survives node replacement),
  // and every code path that reads "what state should this heart be?"
  // checks pendingFlips first. 3.5s TTL covers the slowest observed
  // SDK round-trip + a buffer.
  var pendingFlips = {};                 // pid (string) -> { state, expiresAt }
  var PENDING_FLIP_TTL_MS = 3500;

  // Safari bfcache restore tracker. When the user navigates back to a
  // bfcached collection page, the Wishlist Hero SDK frequently re-emits
  // `wishlist-hero-wishlist-items-from-server` with an empty items
  // cache for 500-1500ms before the real items refetch lands. Without
  // this gate, refineHeartStates flips every filled heart to "empty"
  // during the gap and they visibly flicker for ~1-2s before snapping
  // back. Chrome does not show this because Chrome's bfcache is more
  // selective about when it engages on Shopify storefronts.
  var __bfcacheRestoredAt = 0;
  var BFCACHE_GRACE_MS = 1500;
  try {
    window.addEventListener('pageshow', function (e) {
      if (e && e.persisted) __bfcacheRestoredAt = Date.now();
    });
  } catch (ePs) {}

  // ---- 1st-party wishlist persistence (Safari ITP workaround) -----
  // Wishlist Hero's bundle2.js loads from wishlist-hero.revampco.com — a
  // vendor 3rd-party origin from hringekjan.is's perspective. Safari ITP
  // partitions that origin's localStorage/cookies, so the SDK's
  // GetWishListHash() returns null in Safari and AddWishListItem cannot
  // associate items with any guest identity. Items pile up on the vendor
  // server with no key to retrieve them — the "Uppáhalds" modal always
  // shows empty in Safari guest sessions even though hearts visibly fill.
  //
  // Empirical confirmation (2026-06-10 Chrome MCP vs Safari Web Inspector):
  //   Chrome:  GetWishListHash() → "<base64>", items persist across loads
  //   Safari:  GetWishListHash() → null,        items never persist
  //
  // Workaround: mirror every add/delete into hringekjan.is's own
  // localStorage (1st-party — ITP-safe). On boot, push mirrored items into
  // the SDK's in-memory items list via AddWishListItem so the vendor
  // modal/badge renders them. Chrome stays on the SDK path (cloud sync
  // works); Safari falls through to localStorage transparently.

  var LS_KEY        = 'hringekjan_wlh_items_v1';
  var LS_MAX_BYTES  = 1024 * 1024;

  // How long a mirrored item stays trustworthy without any revalidation.
  //
  // This store is a snapshot: lsAdd records ProductName/Price/ProductLink/
  // ProductImage at click time and NOTHING ever checks them again. On the
  // Safari path the vendor's cloud list is unreachable, so the modal renders
  // straight from this mirror — which means a product that has since been
  // delisted keeps appearing, with the price it had on the day it was
  // hearted. Reported 2026-08-27: "old products are also visible there even
  // though they have dropped off the site."
  //
  // A year is deliberately generous. The honest fix is revalidating each
  // item against the storefront, which costs a request per item on modal
  // open and needs its own design; this bounds the staleness in the
  // meantime without throwing away a list somebody curated. AddedOn has
  // been written since the mirror shipped and never once read — this is
  // the first thing to read it.
  var LS_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

  function lsRead() {
    try {
      var raw = localStorage.getItem(LS_KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];

      // Prune on read rather than on a timer: there is no reliable moment to
      // run a sweep in a page that may never be revisited, and read is the
      // only point where staleness can actually mislead someone.
      //
      // Anything without a PARSEABLE AddedOn is KEPT. Entries written before
      // this field existed have none, and a corrupt value is not evidence
      // that the user stopped wanting the item. Dropping those would delete
      // a curated list on upgrade — a worse failure than the one being fixed.
      var now = Date.now();
      var kept = [];
      for (var i = 0; i < arr.length; i++) {
        var it = arr[i];
        if (!it) continue;
        var t = it.AddedOn ? Date.parse(it.AddedOn) : NaN;
        if (!isNaN(t) && (now - t) > LS_MAX_AGE_MS) continue;
        kept.push(it);
      }
      return kept;
    } catch (e) { return []; }
  }

  function lsWrite(items) {
    try {
      var s = JSON.stringify(items);
      if (s.length > LS_MAX_BYTES) return;   // quota safety
      localStorage.setItem(LS_KEY, s);
    } catch (e) {}
  }

  function lsAdd(payload) {
    if (!payload || payload.ProductId == null) return;
    var items = lsRead();
    var pid = String(payload.ProductId);
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].ProductId) === pid) return;   // already in 1p store
    }
    items.push({
      ProductId:        payload.ProductId,
      ProductVariantId: payload.ProductVariantId,
      ProductName:      payload.ProductTitle,
      Price:            payload.ProductPrice,
      ProductLink:      payload.ProductLink,
      ProductImage:     payload.ProductImage,
      AddedOn:          new Date().toISOString(),
      AddedVia:         'SDK-1p'
    });
    lsWrite(items);
  }

  function lsDelete(productId) {
    if (productId == null) return;
    var items = lsRead();
    var pid = String(productId);
    var next = [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].ProductId) !== pid) next.push(items[i]);
    }
    lsWrite(next);
  }

  // Patch SDK.GetWishListItems to merge SDK + localStorage. Chrome path:
  // SDK returns full cloud-synced list, localStorage is empty (we only
  // write on click after SDK was already going to do it), merge == SDK.
  // Safari path: SDK returns empty (no hash → no identity), localStorage
  // has the user's actions, merge == localStorage. Patch is idempotent.
  //
  // ALSO hydrate SDK's in-memory state via AddWishListItem for each
  // localStorage item — only if SDK list is empty (Safari case). This
  // makes the vendor modal/badge pick up the items even if the modal
  // reads from SDK private state rather than the public getter.
  // Polling attempt counter to bound install1pBacking's setTimeout retries.
  // 40 attempts × 250ms = 10s ceiling — long enough to outlast a slow CDN
  // delivery of the Wishlist Hero bundle, short enough that a permanent
  // SDK-load failure (CSP block, ad-blocker, vendor outage) gives up cleanly
  // instead of polling for the entire page lifetime. Closes
  // `[[2026-06-11-hringekjan-safari-itp-wishlist-review]]` HIGH finding #2.
  var __wlh1pPollAttempts = 0;
  var WLH1P_MAX_POLL_ATTEMPTS = 40;

  function install1pBacking() {
    if (window.__wlh1pInstalled) return;
    var sdk = window.WishListHero_SDK;
    if (!sdk || !sdk.GetWishListItems) {
      __wlh1pPollAttempts++;
      if (__wlh1pPollAttempts >= WLH1P_MAX_POLL_ATTEMPTS) {
        // SDK never loaded — Wishlist Hero bundle blocked or vendor down.
        // Click handler still writes to localStorage on every interaction, so
        // user actions are preserved; they just don't get pushed into the
        // (non-existent) in-memory SDK list. Modal opens to empty either way.
        return;
      }
      setTimeout(install1pBacking, 250);
      return;
    }
    window.__wlh1pInstalled = true;

    // Shared dedupe-merge helper used by all three patched getters.
    // SDK items win on ProductId conflict — they're the authoritative
    // cloud-synced shape with _id, AddedOn, etc.
    function mergeWithLocalStorage(sdkItems) {
      if (!Array.isArray(sdkItems)) sdkItems = [];
      var lsItems = lsRead();
      if (lsItems.length === 0) return sdkItems;
      var seen = {};
      var merged = [];
      for (var i = 0; i < sdkItems.length; i++) {
        var s = sdkItems[i];
        if (!s) continue;
        var sid = String(s.ProductId || s.productId || s.id || '');
        if (sid && !seen[sid]) { seen[sid] = true; merged.push(s); }
      }
      for (var j = 0; j < lsItems.length; j++) {
        var l = lsItems[j];
        var lid = String(l.ProductId || '');
        if (lid && !seen[lid]) { seen[lid] = true; merged.push(l); }
      }
      return merged;
    }

    // Install all three patches via Object.defineProperty with
    // configurable:false getter/setter. Empirically confirmed 2026-06-11
    // in Safari:
    //
    //   1. Vendor's bundle reassigns sdk.GetWishListItems via
    //      Object.defineProperty (NOT plain assignment), which REPLACES our
    //      property descriptor entirely. Our prior version used
    //      configurable:true, so vendor's redefine succeeded — our wrapper
    //      was lost. After the replacement: sdk.GetWishListItems returned
    //      the vendor's function directly, no getter trap.
    //   2. Vendor uses plain `sdk.GetWishListItemsCount = fn` for the
    //      secondary methods — those went through our setter correctly.
    //   3. Our previous Count patch called `sdk.GetWishListItems().length`,
    //      which (after vendor's redefine of GetWishListItems) hit the
    //      vendor's broken-in-Safari implementation → returned 0. The MUI
    //      Dialog modal almost certainly checks count > 0 to decide
    //      between "show items" and "show empty state" → modal stays empty.
    //
    // Two fixes here:
    //
    //   A. configurable:false on the property descriptor. Vendor's
    //      Object.defineProperty redefine now throws TypeError. The vendor
    //      almost certainly catches it (most code that defineProperty's
    //      runtime-mutable globals wraps in try/catch). Our wrapper stays.
    //      If vendor doesn't catch, we have a graver problem the user
    //      will see immediately and can revert. Worth the small risk.
    //   B. Shared `origs` map for all three originals. Each patched
    //      function delegates via `origs[name]` rather than calling
    //      `sdk.X()`. This breaks the "Count depends on GetWishListItems"
    //      chain — patched Count now computes directly from
    //      `origs.GetWishListItems` (which the GetWishListItems setter
    //      updates), even if vendor reassigns and our getter trap somehow
    //      gets bypassed.
    //
    // The `origs` map is updated by the setter for each property. Any
    // future vendor reassignment of these methods captures the new
    // function into origs, and our patched wrappers automatically use
    // the latest.

    var origs = {
      GetWishListItems:           sdk.GetWishListItems,
      GetWishListItemsCount:      sdk.GetWishListItemsCount,
      GetWishListItemsFromServer: sdk.GetWishListItemsFromServer
    };

    function installProperty(name, patched) {
      try {
        Object.defineProperty(sdk, name, {
          configurable: false,
          enumerable: true,
          get: function () { return patched; },
          set: function (newFn) {
            // Vendor (or anyone) reassigned — capture as the new "original".
            // Our getter keeps returning the patched wrapper to all readers.
            origs[name] = newFn;
          }
        });
      } catch (eDefine) {
        // defineProperty can throw if the property is already
        // non-configurable from an earlier install. Fall back to plain
        // assignment (idempotent — second install reuses the same patched
        // wrapper if __wlh1pInstalled is already true).
        try { sdk[name] = patched; } catch (eAssign) {}
      }
    }

    // Patch #1 — sync GetWishListItems
    installProperty('GetWishListItems', function () {
      var sdkItems = [];
      if (typeof origs.GetWishListItems === 'function') {
        try { sdkItems = origs.GetWishListItems.apply(sdk, arguments) || []; } catch (e) {}
      }
      return mergeWithLocalStorage(sdkItems);
    });

    // Patch #2 — sync GetWishListItemsCount (wishlist-icon badge bubble +
    // modal's empty-state check). Computes directly from origs.GetWishListItems
    // + localStorage — does NOT call sdk.GetWishListItems(), which could be
    // intercepted/replaced by future vendor mutations.
    installProperty('GetWishListItemsCount', function () {
      var sdkItems = [];
      if (typeof origs.GetWishListItems === 'function') {
        try { sdkItems = origs.GetWishListItems.apply(sdk, arguments) || []; } catch (e) {}
      }
      return mergeWithLocalStorage(sdkItems).length;
    });

    // Patch #3 — async GetWishListItemsFromServer (MUI Dialog modal items list).
    // Returns Promise<array> resolving to merged list. We still invoke
    // origs.GetWishListItemsFromServer because Chrome's working path may
    // rely on its internal-state side-effects; we only override the
    // returned value.
    installProperty('GetWishListItemsFromServer', function () {
      var origPromise;
      if (typeof origs.GetWishListItemsFromServer === 'function') {
        try { origPromise = origs.GetWishListItemsFromServer.apply(sdk, arguments); }
        catch (eFs) { origPromise = Promise.resolve([]); }
      } else {
        origPromise = Promise.resolve([]);
      }
      var p = (origPromise && typeof origPromise.then === 'function')
        ? origPromise
        : Promise.resolve(origPromise);
      return p.then(mergeWithLocalStorage)
              .catch(function () { return mergeWithLocalStorage([]); });
    });

    // ---- DOM injection fallback for modal items list -----------------
    //
    // Empirical evidence 2026-06-11: even with all three SDK-method
    // patches verified installed and returning merged-with-localStorage
    // data (count=7, modalData=7, sample="Kookaï top"), the vendor's
    // React-rendered MUI Dialog modal STILL renders the "Það eru engar
    // vörur í uppáhalds" empty state. The vendor's React component
    // reads from its own closure-private items state that's populated
    // only by successful cloud responses — not by our patched getters.
    // The hash is null in Safari, the cloud response is {ok:false}, the
    // private state stays []. The modal renders empty regardless of
    // what our SDK patches return.
    //
    // No further SDK-method monkey-patch can reach this. The only path
    // is to inject our items directly into the modal DOM when it opens.
    //
    // Detection: the modal's title element carries class
    // "wishlist-hero-header" (verified in user-shared screenshot:
    // <div class="MuiDialogTitle-root wishlist-hero-header">). When
    // such an element appears in DOM AND the dialog body contains the
    // "engar vörur" / "no items" empty-state text AND localStorage has
    // items, we replace the body content with our own item-list HTML.
    //
    // Idempotency: paper element is tagged with __wlh1pInjected after
    // first injection so React reconciliation doesn't trigger re-inject
    // on every mutation tick. If the modal closes and reopens with a
    // new paper element, the tag is absent on the new element and
    // injection runs again.

    // Escape a value for use inside a double-quoted HTML attribute. The old
    // code replaced only `"` here, which closes the attribute but leaves `<`
    // and `>` free to open a new tag — and this string goes into innerHTML/
    // outerHTML. `&` must go first or it would double-encode the entities
    // introduced by the replacements after it.
    function attr(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    // Only ever emit a URL we are willing to navigate to or fetch.
    //
    // These values come from the site's own product data, so this is not a
    // live exploit today — but the store is localStorage, which is writable
    // by anything running on the origin, and the sink is innerHTML. An
    // allowlist costs nothing and removes the question. Relative paths (the
    // normal case, "/products/x") and the protocol-relative CDN form
    // ("//cdn.shopify.com/...") are what the mirror actually stores.
    function safeUrl(value, fallback) {
      var raw = String(value == null ? '' : value).trim();
      if (!raw) return fallback;
      if (raw.indexOf('//') === 0) return 'https:' + raw;
      if (raw.charAt(0) === '/') return raw;
      if (/^https?:\/\//i.test(raw)) return raw;
      // javascript:, data:, vbscript:, and anything else unrecognised.
      return fallback;
    }

    function buildModalItemListHTML(items) {
      var parts = ['<div data-wlh-1p-list="1" style="padding:8px 16px 16px;max-height:60vh;overflow-y:auto;">'];
      for (var i = 0; i < items.length; i++) {
        var it = items[i] || {};
        var img = attr(safeUrl(it.ProductImage, ''));
        var link = attr(safeUrl(it.ProductLink, '#'));
        var pid = attr(it.ProductId == null ? '' : it.ProductId);
        var name = String(it.ProductName || '').replace(/[<>&]/g, function (ch) {
          return ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&amp;';
        });
        var price = it.Price ? (it.Price + ' ISK') : '';
        // The remove control. A real <button> with an accessible name, not a
        // styled <span>: the bug being fixed is already "a control the user
        // cannot operate", and a div with a click handler would reproduce
        // that for anyone on a keyboard or a screen reader. 44px square is
        // the minimum comfortable touch target — this list is reached almost
        // exclusively from a phone.
        parts.push(
          '<div data-wlh-row="' + pid + '" style="display:flex;gap:12px;padding:12px 0;border-bottom:1px solid #eee;align-items:center;">' +
            '<a href="' + link + '" style="flex-shrink:0;display:block;">' +
              '<img src="' + img + '" loading="lazy" alt="" ' +
                'style="width:84px;height:84px;object-fit:cover;border-radius:4px;display:block;">' +
            '</a>' +
            '<div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">' +
              '<a href="' + link + '" style="color:inherit;text-decoration:none;font-weight:500;font-size:14px;line-height:1.3;margin-bottom:6px;">' +
                name +
              '</a>' +
              '<div style="color:#666;font-size:13px;">' + price + '</div>' +
            '</div>' +
            '<button type="button" data-wlh-remove="' + pid + '" ' +
              'aria-label="Fjarlægja ' + attr(it.ProductName || 'vöru') + ' úr uppáhalds" ' +
              'style="flex-shrink:0;width:44px;height:44px;border:0;background:none;cursor:pointer;' +
              'font-size:22px;line-height:1;color:#888;padding:0;">&times;</button>' +
          '</div>'
        );
      }
      parts.push('</div>');
      return parts.join('');
    }

    // Make the injected rows removable.
    //
    // This is the half that was missing. The injected list rendered correctly
    // and could not be edited: lsDelete() has always been correct, but its
    // ONLY call site is the heart on a product card, which does not exist
    // inside this modal. So on the Safari path — the only path where this
    // list is what the user sees — the wishlist was append-only. Reported
    // 2026-08-27: "I cannot take anything out of it."
    //
    // Delegated from the paper element rather than bound per button: the
    // rows are re-rendered on every re-injection, and per-button listeners
    // would leak one per pass and go stale the moment the list is rebuilt.
    function installModalRemoveHandler(paper) {
      if (!paper || paper.__wlh1pRemoveBound) return;
      paper.__wlh1pRemoveBound = true;

      paper.addEventListener('click', function (ev) {
        var btn = ev.target && ev.target.closest && ev.target.closest('[data-wlh-remove]');
        if (!btn || !paper.contains(btn)) return;

        // The vendor's dialog has its own handlers on this subtree; do not
        // let the click reach them and do not let it navigate.
        ev.preventDefault();
        ev.stopPropagation();

        var pid = btn.getAttribute('data-wlh-remove');
        if (!pid) return;

        // SAME ORDERING AS THE CARD HEART (line ~509) AND FOR THE SAME
        // REASON: 1st-party mirror FIRST, vendor SDK second in its own
        // try-block. The vendor call throws synchronously on Safari when
        // there is no guest hash, and if lsDelete ran after it inside a
        // shared try the throw would skip it — the row would vanish from
        // the DOM and come back on reopen. That is the Layer-1 bug from
        // the June arc, re-introduced in a new place.
        lsDelete(pid);

        try {
          var sdk = window.WishListHero_SDK;
          if (sdk && sdk.DeleteWishListItem) {
            sdk.DeleteWishListItem({ ProductId: pid });
          }
        } catch (eSdk) {
          // Vendor threw — the 1st-party delete above already happened,
          // which is the copy this modal actually renders from.
        }

        // Drop the row immediately. Re-running the whole injection would be
        // cleaner but the vendor re-renders asynchronously and would fight
        // us; removing the row we own is instant and cannot desync from
        // localStorage, which was just written.
        var row = btn.closest('[data-wlh-row]');
        if (row && row.parentElement) row.parentElement.removeChild(row);

        // Last row gone: clear the idempotency tag so the next open
        // re-evaluates from scratch and shows the vendor's real empty state
        // rather than an empty box we injected.
        if (lsRead().length === 0) {
          paper.__wlh1pInjected = false;
        }

        // Keep the hearts on the page behind the modal honest.
        try { refineHeartStates(); } catch (eRefine) {}
      }, true);
    }

    function injectIntoModalIfEmpty() {
      try {
        var lsItems = lsRead();
        if (lsItems.length === 0) return;

        var headers = document.querySelectorAll('[class*="wishlist-hero-header"]');
        for (var i = 0; i < headers.length; i++) {
          var header = headers[i];
          // Walk up to the MuiPaper (the modal panel containing both title + body).
          var paper = header.closest && (header.closest('[class*="MuiPaper"]') ||
                                         header.closest('[class*="MuiDialog"]') ||
                                         header.closest('[role="dialog"]'));
          if (!paper) paper = header.parentElement;
          if (!paper || paper.__wlh1pInjected) continue;

          // Locate the dialog body. Try MuiDialogContent first, then fall
          // back to the paper itself (we'll inject before the share button).
          var content = paper.querySelector('[class*="MuiDialogContent"]') ||
                        paper.querySelector('[class*="DialogContent"]') ||
                        paper;

          var contentText = (content.innerText || content.textContent || '').toLowerCase();
          // Match Icelandic ("engar vörur"), English ("no items"), and
          // generic empty-state markers. If the modal already has items
          // rendered (Chrome's working path), we skip — vendor content stays.
          var looksEmpty = contentText.indexOf('engar vörur') !== -1 ||
                           contentText.indexOf('no items') !== -1 ||
                           contentText.indexOf('empty') !== -1 ||
                           contentText.replace(/\s/g, '').length < 5;
          if (!looksEmpty) continue;

          // Find the smallest container that holds the empty-state text
          // (so we replace JUST that subtree, not the dialog title or the
          // footer share button).
          var emptyContainer = null;
          var walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, {
            acceptNode: function (n) {
              var t = (n.textContent || '').toLowerCase();
              return (t.indexOf('engar vörur') !== -1 ||
                      t.indexOf('no items') !== -1)
                ? NodeFilter.FILTER_ACCEPT
                : NodeFilter.FILTER_REJECT;
            }
          });
          var hit = walker.nextNode();
          if (hit) {
            emptyContainer = hit.parentElement;
            // Walk up a level or two so we replace the empty-state container
            // (not just the text node), but stay within content.
            for (var up = 0; up < 3 && emptyContainer && emptyContainer.parentElement &&
                              emptyContainer.parentElement !== content; up++) {
              emptyContainer = emptyContainer.parentElement;
            }
          }

          var listHTML = buildModalItemListHTML(lsItems);
          if (emptyContainer && emptyContainer !== content) {
            emptyContainer.outerHTML = listHTML;
          } else {
            // Couldn't find the empty-state subtree — fall back to prepending
            // into content. Preserves any footer (DEILA button).
            var listEl = document.createElement('div');
            listEl.innerHTML = listHTML;
            content.insertBefore(listEl.firstChild, content.firstChild);
          }
          paper.__wlh1pInjected = true;
          installModalRemoveHandler(paper);
        }
      } catch (eInj) {
        // Fail open — the modal stays empty rather than the storefront crashing.
      }
    }

    // MutationObserver: when Wishlist Hero's modal mounts (added node
    // matches/contains [class*="wishlist-hero"]), schedule an injection
    // pass. Small delay lets React finish its initial render so we have
    // the empty-state text to replace.
    try {
      var modalObserver = new MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var added = mutations[m].addedNodes;
          for (var n = 0; n < added.length; n++) {
            var node = added[n];
            if (!node || node.nodeType !== 1) continue;
            var matchesWlh =
              (node.matches && node.matches('[class*="wishlist-hero"]')) ||
              (node.querySelector && node.querySelector('[class*="wishlist-hero"]'));
            if (matchesWlh) {
              // Two-pass injection: immediate (catches synchronous render)
              // + 250ms later (catches React's async commit).
              setTimeout(injectIntoModalIfEmpty, 0);
              setTimeout(injectIntoModalIfEmpty, 250);
              return;
            }
          }
        }
      });
      modalObserver.observe(document.body, { childList: true, subtree: true });
    } catch (eObs) {
      // No MutationObserver support — graceful degradation.
    }

    // Hydrate SDK's in-memory items array from localStorage — ONLY when we
    // have a positive signal that the SDK has no persistent identity (i.e.
    // Safari Intelligent Tracking Prevention killed the guest hash). The
    // earlier guard `sdkOwn.length === 0` fired on far more cases than just
    // Safari — vendor server purges, fresh logged-in customers whose cloud
    // list is genuinely empty, cleared cookies, brand-new devices — any of
    // those would have triggered duplicate AddWishListItem cloud writes on
    // Chrome. The Safari signature is more specific: GetWishListHash() returns
    // null because the SDK can't persist the guest hash to its partitioned
    // 3rd-party storage. On Chrome the hash is always a base64 string.
    // Closes `[[2026-06-11-hringekjan-safari-itp-wishlist-review]]` HIGH
    // finding #1 (false-positive hydration → Chrome duplicate writes).
    try {
      var sdkOwn = [];
      try { sdkOwn = origGet.call(sdk) || []; } catch (e0) {}
      var sdkHash = '';
      try { sdkHash = (sdk.GetWishListHash && sdk.GetWishListHash()) || ''; } catch (eH) {}
      var safariITP = (sdkHash === '' || sdkHash == null);
      if (Array.isArray(sdkOwn) && sdkOwn.length === 0 && safariITP) {
        var lsItems = lsRead();
        for (var k = 0; k < lsItems.length; k++) {
          var it = lsItems[k];
          if (sdk.AddWishListItem) {
            try {
              sdk.AddWishListItem({
                ProductId:           it.ProductId,
                ProductVariantId:    it.ProductVariantId,
                ProductTitle:        it.ProductName,
                ProductPrice:        it.Price,
                ProductLink:         it.ProductLink,
                ProductImage:        it.ProductImage,
                WishlistHash:        (sdk.GetWishListHash && sdk.GetWishListHash()) || '',
                CustomerId:          (typeof sdk.GetCustomerId === 'function' ? sdk.GetCustomerId() : 0) || 0,
                ButtonClassElement:  null,
                ButtonMode:          'icon_only'
              });
            } catch (eHyOne) {}
          }
        }
      }
    } catch (eHy) {}
  }

  function recordPendingFlip(pid, state) {
    if (pid == null) return;
    pendingFlips[String(pid)] = { state: state, expiresAt: Date.now() + PENDING_FLIP_TTL_MS };
  }

  function peekPendingFlip(pid) {
    // Returns the pinned state if a fresh flip exists for this pid,
    // else null. Does NOT consume — multiple readers can call it
    // within the TTL window. GCs expired entries as it walks.
    if (pid == null) return null;
    var key = String(pid);
    var entry = pendingFlips[key];
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) { delete pendingFlips[key]; return null; }
    return entry.state;
  }

  // Canonical "what state should this heart be in RIGHT NOW?" lookup.
  // Recent user click wins over the data attribute (which Wishlist Hero
  // may have stomped). Also opportunistically restores the attribute
  // if it drifted — keeps the watchdog's attribute-based fill correct.
  function getEffectiveState(heart) {
    try {
      var pid = heart.getAttribute('data-wlh-id');
      var pinned = peekPendingFlip(pid);
      if (pinned) {
        if (heart.getAttribute('data-wlh-item-state') !== pinned) {
          heart.setAttribute('data-wlh-item-state', pinned);
        }
        return pinned;
      }
      return heart.getAttribute('data-wlh-item-state') || 'empty';
    } catch (e) {
      return 'empty';
    }
  }

  // ---- Per-heart state reconciliation ----------------------
  // Server-of-truth pass: for every heart on the page, check if
  // its product id is currently in the wishlist and update the
  // data-wlh-item-state attribute. Called on boot AND whenever
  // Wishlist Hero dispatches an add/delete/load event.
  function refineHeartStates() {
    try {
      var sdk = window.WishListHero_SDK;
      var items = (sdk && typeof sdk.GetWishListItems === 'function')
        ? sdk.GetWishListItems()
        : null;
      if (!Array.isArray(items)) items = [];
      // Safari bfcache guard: within BFCACHE_GRACE_MS of a Back-nav
      // restore, an empty items snapshot is overwhelmingly likely to
      // be the SDK's re-init transient (real cache not yet refetched),
      // not the truth. Trust the existing DOM state instead of
      // flipping every filled heart to empty for a visible flicker.
      if (items.length === 0 &&
          __bfcacheRestoredAt &&
          (Date.now() - __bfcacheRestoredAt) < BFCACHE_GRACE_MS) {
        return;
      }
      // Build a Set of product ids for O(1) lookup
      var inSet = {};
      for (var i = 0; i < items.length; i++) {
        var iid = items[i] && (items[i].ProductId || items[i].productId || items[i].id);
        if (iid != null) inSet[String(iid)] = true;
      }
      var hearts = document.querySelectorAll(SELECTOR);
      for (var h = 0; h < hearts.length; h++) {
        var pid = hearts[h].getAttribute('data-wlh-id');
        var serverState = (pid && inSet[String(pid)]) ? 'filled' : 'empty';
        // Within the grace window after a user click, trust the
        // click over the (possibly-stale) server snapshot.
        var pinned = peekPendingFlip(pid);
        var newState = pinned || serverState;
        if (hearts[h].getAttribute('data-wlh-item-state') !== newState) {
          hearts[h].setAttribute('data-wlh-item-state', newState);
        }
        // ALWAYS apply fill (in case state was set but SVG fill drifted)
        applyStateFill(hearts[h], newState);
      }
    } catch (refineErr) {}
  }

  function installStateEventListeners() {
    if (window.__wlhStateListenersInstalled) return;
    window.__wlhStateListenersInstalled = true;
    // CRITICAL: only refine on DEFINITIVE server events. Refining
    // on user-initiated add/delete fights with the optimistic
    // toggle — bundle2 hasn't always persisted by the time we
    // re-check GetWishListItems(), so we'd revert the green
    // back to empty. The click handler is the source of truth
    // for user actions; this listener is for cross-tab / cross-
    // surface sync (e.g. when the wishlist popup deletes an item).
    document.addEventListener(
      'wishlist-hero-wishlist-items-from-server',
      function () { setTimeout(refineHeartStates, 200); }
    );
    // Delete-all is also worth catching (user clicked "remove
    // all" in the popup) — but with a longer delay since the
    // SDK round-trip is heavier here.
    document.addEventListener(
      'wishlist-hero-wishlist-delete-all-items',
      function () { setTimeout(refineHeartStates, 1500); }
    );
  }

  // Periodic re-fill: cheap watchdog. Wishlist Hero's bundle2 may
  // re-render the heart's inner SVG when state changes, which
  // wipes our setAttribute('fill', ...) — the path comes back as
  // fill="currentColor" → resolves to Wishlist Hero's navy.
  // A 600ms re-apply pass keeps every heart's fill matched to its
  // data-wlh-item-state without depending on catching every Wishlist
  // Hero internal re-render via MutationObserver.
  function startFillWatchdog() {
    if (window.__wlhFillWatchdogStarted) return;
    window.__wlhFillWatchdogStarted = true;
    // Faster interval during initial grace window — the SDK race we're
    // defending against typically settles in <2s, so a 250ms tick keeps
    // the visible "flicker window" sub-frame for the user.
    setInterval(function () {
      try {
        var hearts = document.querySelectorAll(SELECTOR);
        for (var i = 0; i < hearts.length; i++) {
          var st = getEffectiveState(hearts[i]);   // pendingFlips wins
          var p = hearts[i].querySelector('svg path');
          var currentAttr = p && p.getAttribute('fill');
          var expected = (st === 'filled') ? '#47A161' : 'none';
          if (currentAttr !== expected) {
            applyStateFill(hearts[i], st);
          }
        }
      } catch (e) {}
    }, 250);
  }

  function boot() {
    try {
      injectStyleOnce();
      installClickInterceptor();
      installStateEventListeners();
      install1pBacking();    // Safari ITP workaround — patches SDK + hydrates from localStorage
      sweep(document);       // catch already-mounted buttons
      installObserver();     // catch future mutations
      startFillWatchdog();   // 600ms polling re-fill (cheap)
      // Belt-and-braces: a couple of delayed re-sweeps in case
      // bundle2.js writes inline styles in a microtask just
      // after we attach the observer (observer catches it,
      // but a follow-up sweep covers any race we missed).
      setTimeout(function () { sweep(document); refineHeartStates(); }, 250);
      setTimeout(function () { sweep(document); refineHeartStates(); }, 1500);
      // Initial state refinement after SDK has loaded items
      setTimeout(refineHeartStates, 50);
    } catch (bootErr) {
      // Never break the storefront.
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
